import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, UserPlus, AlertCircle } from 'lucide-react';

const RoadmapCreator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const devisId = searchParams.get('devisId');
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [prestataires, setPrestataires] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [newClientMode, setNewClientMode] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  const [newClient, setNewClient] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: ''
  });
  
  const [formData, setFormData] = useState({
    event_title: '',
    event_date: '',
    days: [
      {
        dayLabel: 'JOUR 1',
        date: '',
        events: [
          {
            title: '',
            heure: '',
            lieu: '',
            adresse: '',
            notes: '',
            prestataires: []
          }
        ]
      }
    ],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (devisId) {
      loadDevisData();
    }
  }, [devisId]);

  const loadData = async () => {
    try {
      const [clientsRes, prestRes, rolesRes] = await Promise.all([
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/prestataires`, { withCredentials: true }),
        axios.get(`${API}/prestataire-roles`)
      ]);
      
      setClients(clientsRes.data);
      setPrestataires(prestRes.data);
      setRoleOptions(rolesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const loadDevisData = async () => {
    try {
      const res = await axios.get(`${API}/devis/${devisId}`, { withCredentials: true });
      const devis = res.data;
      
      // Préremplir avec les données du devis
      setFormData(prev => ({
        ...prev,
        event_title: `${devis.event_info.event_type} de ${devis.event_info.vedettes}`,
        event_date: devis.event_info.dates[0]?.date || '',
        days: devis.event_info.dates.map((dateInfo, index) => ({
          dayLabel: `JOUR ${index + 1}`,
          date: dateInfo.date,
          events: [{
            title: dateInfo.type,
            heure: '',
            lieu: dateInfo.lieu || '',
            adresse: '',
            notes: '',
            prestataires: []
          }]
        }))
      }));
      
      // Sélectionner le client du devis
      const clientId = devis.client_info.id || devis.client_info.email;
      setSelectedClient(clientId);
      
      toast.success('Données du devis chargées');
    } catch (error) {
      console.error('Erreur chargement devis:', error);
      toast.error('Erreur lors du chargement du devis');
    }
  };

  const checkDuplicate = async (email, telephone) => {
    if (!email && !telephone) return;
    
    try {
      const res = await axios.post(`${API}/clients/check-duplicate`, { email, telephone });
      if (res.data.exists) {
        setDuplicateWarning(res.data.client);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
    }
  };

  const handleNewClientChange = (field, value) => {
    const updated = { ...newClient, [field]: value };
    setNewClient(updated);
    
    // Check for duplicates when email or phone changes
    if (field === 'email' || field === 'telephone') {
      checkDuplicate(updated.email, updated.telephone);
    }
  };

  const addDay = () => {
    setFormData({
      ...formData,
      days: [
        ...formData.days,
        {
          dayLabel: `JOUR ${formData.days.length + 1}`,
          date: '',
          events: [
            {
              title: '',
              heure: '',
              lieu: '',
              adresse: '',
              notes: '',
              prestataires: []
            }
          ]
        }
      ]
    });
  };

  const removeDay = (dayIndex) => {
    if (formData.days.length === 1) {
      toast.error('Il faut au moins un jour');
      return;
    }
    setFormData({
      ...formData,
      days: formData.days.filter((_, i) => i !== dayIndex)
    });
  };

  const updateDay = (dayIndex, field, value) => {
    const newDays = [...formData.days];
    newDays[dayIndex] = { ...newDays[dayIndex], [field]: value };
    setFormData({ ...formData, days: newDays });
  };

  const addEvent = (dayIndex) => {
    const newDays = [...formData.days];
    newDays[dayIndex].events.push({
      title: '',
      heure: '',
      lieu: '',
      adresse: '',
      notes: '',
      prestataires: []
    });
    setFormData({ ...formData, days: newDays });
  };

  const removeEvent = (dayIndex, eventIndex) => {
    const newDays = [...formData.days];
    if (newDays[dayIndex].events.length === 1) {
      toast.error('Il faut au moins un événement par jour');
      return;
    }
    newDays[dayIndex].events = newDays[dayIndex].events.filter((_, i) => i !== eventIndex);
    setFormData({ ...formData, days: newDays });
  };

  const updateEvent = (dayIndex, eventIndex, field, value) => {
    const newDays = [...formData.days];
    newDays[dayIndex].events[eventIndex] = {
      ...newDays[dayIndex].events[eventIndex],
      [field]: value
    };
    setFormData({ ...formData, days: newDays });
  };

  const addPrestataireToEvent = (dayIndex, eventIndex) => {
    const newDays = [...formData.days];
    newDays[dayIndex].events[eventIndex].prestataires.push({
      role: '',
      name: '',
      prestataire_id: ''
    });
    setFormData({ ...formData, days: newDays });
  };

  const removePrestataireFromEvent = (dayIndex, eventIndex, prestIndex) => {
    const newDays = [...formData.days];
    newDays[dayIndex].events[eventIndex].prestataires = 
      newDays[dayIndex].events[eventIndex].prestataires.filter((_, i) => i !== prestIndex);
    setFormData({ ...formData, days: newDays });
  };

  const updatePrestataire = (dayIndex, eventIndex, prestIndex, field, value) => {
    const newDays = [...formData.days];
    newDays[dayIndex].events[eventIndex].prestataires[prestIndex] = {
      ...newDays[dayIndex].events[eventIndex].prestataires[prestIndex],
      [field]: value
    };
    
    // Auto-fill name when prestataire is selected
    if (field === 'prestataire_id' && value) {
      const prest = prestataires.find(p => p.id === value);
      if (prest) {
        newDays[dayIndex].events[eventIndex].prestataires[prestIndex].name = 
          `${prest.prenom} ${prest.nom}`;
      }
    }
    
    setFormData({ ...formData, days: newDays });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newClientMode && !selectedClient) {
      toast.error('Veuillez sélectionner ou créer un client');
      return;
    }
    
    if (newClientMode) {
      if (!newClient.nom || !newClient.prenom || !newClient.email || !newClient.telephone) {
        toast.error('Veuillez remplir tous les champs obligatoires du client');
        return;
      }
      
      if (duplicateWarning) {
        toast.error('Ce client existe déjà. Utilisez la sélection de client existant.');
        return;
      }
    }
    
    setSaving(true);
    try {
      let clientInfo;
      
      // Create client if in new client mode
      if (newClientMode) {
        const clientRes = await axios.post(`${API}/clients`, newClient, { withCredentials: true });
        clientInfo = clientRes.data;
      } else {
        const client = clients.find(c => c.id === selectedClient);
        clientInfo = client;
      }
      
      // Convert days structure to flat blocks for backend
      const blocks = [];
      formData.days.forEach(day => {
        day.events.forEach(event => {
          blocks.push({
            title: event.title,
            date: day.date,
            heure: event.heure,
            lieu: event.lieu,
            adresse: event.adresse,
            notes: event.notes,
            prestataires: event.prestataires
          });
        });
      });
      
      const roadmapData = {
        event_title: formData.event_title,
        event_date: formData.event_date,
        blocks: blocks,
        notes: formData.notes,
        client_info: {
          id: clientInfo.id,
          nom: clientInfo.nom,
          prenom: clientInfo.prenom,
          email: clientInfo.email,
          telephone: clientInfo.telephone,
          adresse: clientInfo.adresse,
          ville: clientInfo.ville,
          code_postal: clientInfo.code_postal
        }
      };
      
      await axios.post(`${API}/roadmaps/standalone`, roadmapData, { withCredentials: true });
      toast.success('Feuille de route créée avec succès');
      navigate('/backoffice/roadmaps');
    } catch (error) {
      toast.error('Erreur lors de la création : ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <Button variant="outline" size="sm" onClick={() => navigate('/backoffice/roadmaps')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-stone-800 mb-6">Créer une feuille de route</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <Card>
          <CardHeader>
            <CardTitle>1. Sélectionner ou créer un client</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Button
                type="button"
                variant={!newClientMode ? 'default' : 'outline'}
                onClick={() => setNewClientMode(false)}
              >
                Sélectionner un client existant
              </Button>
              <Button
                type="button"
                variant={newClientMode ? 'default' : 'outline'}
                onClick={() => setNewClientMode(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Créer un nouveau client
              </Button>
            </div>

            {!newClientMode ? (
              <div>
                <Label>Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.prenom} {client.nom} ({client.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-4">
                {duplicateWarning && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Ce client existe déjà : {duplicateWarning.prenom} {duplicateWarning.nom} ({duplicateWarning.email}, {duplicateWarning.telephone})
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Prénom *</Label>
                    <Input
                      value={newClient.prenom}
                      onChange={(e) => handleNewClientChange('prenom', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Nom *</Label>
                    <Input
                      value={newClient.nom}
                      onChange={(e) => handleNewClientChange('nom', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => handleNewClientChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input
                      value={newClient.telephone}
                      onChange={(e) => handleNewClientChange('telephone', e.target.value)}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Adresse</Label>
                    <Input
                      value={newClient.adresse}
                      onChange={(e) => handleNewClientChange('adresse', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Code Postal</Label>
                    <Input
                      value={newClient.code_postal}
                      onChange={(e) => handleNewClientChange('code_postal', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input
                      value={newClient.ville}
                      onChange={(e) => handleNewClientChange('ville', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Event Info */}
        <Card>
          <CardHeader>
            <CardTitle>2. Informations de l'événement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Titre de l'événement *</Label>
              <Input
                value={formData.event_title}
                onChange={(e) => setFormData({ ...formData, event_title: e.target.value })}
                placeholder="Ex: Mariage Sarah & David"
                required
              />
            </div>
            <div>
              <Label>Date principale *</Label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Days & Events */}
        <div className="space-y-4">
          {formData.days.map((day, dayIndex) => (
            <Card key={dayIndex}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{day.dayLabel}</CardTitle>
                  {formData.days.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDay(dayIndex)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Date de ce jour</Label>
                  <Input
                    type="date"
                    value={day.date}
                    onChange={(e) => updateDay(dayIndex, 'date', e.target.value)}
                  />
                </div>

                {/* Events in this day */}
                <div className="space-y-4 border-l-2 border-amber-200 pl-4">
                  {day.events.map((event, eventIndex) => (
                    <div key={eventIndex} className="space-y-3 p-4 bg-stone-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-sm text-stone-700">Événement {eventIndex + 1}</h4>
                        {day.events.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeEvent(dayIndex, eventIndex)}
                          >
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Titre de l'événement</Label>
                          <Input
                            value={event.title}
                            onChange={(e) => updateEvent(dayIndex, eventIndex, 'title', e.target.value)}
                            placeholder="Ex: Synagogue, Brunch, Henné"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Heure</Label>
                          <Input
                            type="time"
                            value={event.heure}
                            onChange={(e) => updateEvent(dayIndex, eventIndex, 'heure', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Lieu</Label>
                          <Input
                            value={event.lieu}
                            onChange={(e) => updateEvent(dayIndex, eventIndex, 'lieu', e.target.value)}
                            placeholder="Nom du lieu"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Adresse</Label>
                          <Input
                            value={event.adresse}
                            onChange={(e) => updateEvent(dayIndex, eventIndex, 'adresse', e.target.value)}
                            placeholder="Adresse complète"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Notes</Label>
                          <Textarea
                            value={event.notes}
                            onChange={(e) => updateEvent(dayIndex, eventIndex, 'notes', e.target.value)}
                            rows={2}
                          />
                        </div>
                      </div>

                      {/* Prestataires for this event */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs">Prestataires</Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => addPrestataireToEvent(dayIndex, eventIndex)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Prestataire
                          </Button>
                        </div>

                        {event.prestataires.map((prest, prestIndex) => (
                          <div key={prestIndex} className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Select
                                value={prest.role}
                                onValueChange={(val) => updatePrestataire(dayIndex, eventIndex, prestIndex, 'role', val)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Rôle" />
                                </SelectTrigger>
                                <SelectContent>
                                  {roleOptions.map(role => (
                                    <SelectItem key={role.id} value={role.label}>{role.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1">
                              <Select
                                value={prest.prestataire_id}
                                onValueChange={(val) => updatePrestataire(dayIndex, eventIndex, prestIndex, 'prestataire_id', val)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Prestataire" />
                                </SelectTrigger>
                                <SelectContent>
                                  {prestataires.map(p => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.prenom} {p.nom}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removePrestataireFromEvent(dayIndex, eventIndex, prestIndex)}
                            >
                              <Trash2 className="h-4 w-4 text-rose-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addEvent(dayIndex)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un événement à ce jour
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          <Button type="button" variant="outline" onClick={addDay} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un jour
          </Button>
        </div>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notes générales</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Notes supplémentaires..."
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-4">
          <Button type="submit" disabled={saving} className="flex-1">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Création en cours...' : 'Créer la feuille de route'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/backoffice/roadmaps')}>
            Annuler
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RoadmapCreator;
